import { X, Check, Pause, SkipForward, Phone, Link2, Play, Sparkles, Minimize2 } from 'lucide-react';

/**
 * Pure presentational pieces of the FocusMode modal. Keeping them out of the
 * main component lets it focus on timer state + completion side-effects.
 */

export function FocusTimerPanel({
  minutes, seconds, totalMinutes,
  progress, bizColor, bizCode,
  isPaused, onTogglePause, onComplete, onSkip,
}) {
  const radius = 100;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - progress);

  return (
    <div className="rounded-[28px] p-5" style={{ background: 'var(--pd-panel-2)', border: '1px solid var(--pd-border)' }}>
      <div className="relative mb-6 flex items-center justify-center">
        <svg width="224" height="224" className="-rotate-90">
          <circle cx="112" cy="112" r={radius} fill="none" stroke="var(--pd-border)" strokeWidth="8" />
          <circle cx="112" cy="112" r={radius} fill="none" stroke={bizColor} strokeWidth="8" strokeDasharray={circumference} strokeDashoffset={dashOffset} strokeLinecap="round" style={{ transition: 'stroke-dashoffset 1s linear' }} />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-5xl font-light tabular-nums" style={{ color: 'var(--pd-text)' }}>
            {minutes.toString().padStart(2, '0')}:{seconds.toString().padStart(2, '0')}
          </span>
          <span className="text-sm mt-1" style={{ color: 'var(--pd-muted)' }}>of {totalMinutes}:00 remaining</span>
        </div>
      </div>
      {bizCode ? <span className="assistant-chip" style={{ borderColor: `${bizColor}33`, color: bizColor }}>{bizCode}</span> : null}
      <div className="grid gap-2 mt-6">
        <button data-testid="focus-complete-button" onClick={onComplete} className="px-5 py-3 rounded-[18px] text-sm font-medium text-white flex items-center justify-between" style={{ background: 'var(--pd-success)' }}>
          <span>Complete</span><Check className="w-4 h-4" />
        </button>
        <button data-testid="focus-pause-button" onClick={onTogglePause} className="px-5 py-3 rounded-[18px] text-sm font-medium flex items-center justify-between btn-ghost">
          {isPaused ? <><span>Resume</span><Play className="w-4 h-4" /></> : <><span>Pause</span><Pause className="w-4 h-4" /></>}
        </button>
        <button data-testid="focus-skip-button" onClick={onSkip} className="px-5 py-3 rounded-[18px] text-sm font-medium flex items-center justify-between btn-ghost">
          <span>Skip</span><SkipForward className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export function FocusContextPanel({ task, notes, setNotes }) {
  const showCall = task?.taskType === 'CALL';
  const showLink = task?.taskType === 'READING' && task?.sourceUrl;
  if (!showCall && !showLink) return null;
  return (
    <div className="rounded-[28px] p-5" style={{ background: 'var(--pd-panel)', border: '1px solid var(--pd-border)' }}>
      {showCall ? (
        <div className="flex items-center gap-2 text-[12px] font-semibold" style={{ color: 'var(--pd-text)' }}>
          <Phone className="w-4 h-4" style={{ color: 'var(--pd-focus)' }} />Call context
        </div>
      ) : (
        <a href={task.sourceUrl} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[12px] font-semibold" style={{ color: 'var(--pd-focus)' }}>
          <Link2 className="w-4 h-4" />Open source link
        </a>
      )}
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder={showCall ? 'Call notes...' : 'Key takeaways...'}
        rows={4}
        className="w-full px-4 py-3 rounded-[18px] text-sm resize-none mt-4 outline-none"
        style={{ border: '1px solid var(--pd-border)', background: 'var(--pd-panel-solid)', color: 'var(--pd-text)' }}
      />
    </div>
  );
}

export function FocusChecklist({ subtasks, onToggle }) {
  if (!subtasks?.length) return null;
  return (
    <div className="rounded-[28px] p-5" style={{ background: 'var(--pd-panel)', border: '1px solid var(--pd-border)' }}>
      <div className="text-[12px] font-semibold" style={{ color: 'var(--pd-text)' }}>Checklist</div>
      <div className="space-y-2 mt-4">
        {subtasks.map((subtask, index) => (
          <button
            key={subtask.id || `subtask-${subtask.title}`}
            onClick={() => onToggle(index)}
            className="w-full flex items-center gap-3 p-3 rounded-[18px] text-left transition-colors"
            style={{ background: 'var(--pd-panel-2)', border: '1px solid var(--pd-border)' }}
          >
            <div
              className="w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0"
              style={{
                borderColor: subtask.completed ? 'var(--pd-success)' : 'var(--pd-border)',
                background: subtask.completed ? 'var(--pd-success)' : 'transparent',
              }}
            >
              {subtask.completed ? <Check className="w-3 h-3 text-white" strokeWidth={3} /> : null}
            </div>
            <span
              className="text-sm"
              style={{
                color: 'var(--pd-text)',
                textDecoration: subtask.completed ? 'line-through' : 'none',
                opacity: subtask.completed ? 0.6 : 1,
              }}
            >{subtask.title}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export function FocusCoachReminder() {
  return (
    <div className="rounded-[28px] p-5" style={{ background: 'var(--pd-panel-2)', border: '1px solid var(--pd-border)' }}>
      <div className="text-[12px] font-semibold" style={{ color: 'var(--pd-text)' }}>Coach reminder</div>
      <p className="text-[12px] mt-3" style={{ color: 'var(--pd-muted)', lineHeight: 1.8 }}>
        Stay with the current task until a meaningful checkpoint is complete. Use notes only to protect future memory, not to escape the work.
      </p>
    </div>
  );
}

export function FocusHeader({ task, bizColor, onClose, onMinimize }) {
  return (
    <div className="flex items-center justify-between gap-4 mb-6">
      <div>
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4" style={{ color: bizColor }} />
          <span className="text-[10px] uppercase tracking-[0.14em] font-semibold" style={{ color: 'var(--pd-subtle)' }}>Deep work initiated</span>
        </div>
        <h2 className="text-[26px] font-medium mt-3" style={{ color: 'var(--pd-text)', letterSpacing: '-0.05em' }}>{task?.title}</h2>
      </div>
      <div className="flex items-center gap-1">
        {onMinimize ? (
          <button
            data-testid="focus-minimize"
            onClick={onMinimize}
            className="pd-icon-button"
            title="Minimize — keep the timer running and watch the row glow"
            aria-label="Minimize focus view"
          >
            <Minimize2 className="w-5 h-5" />
          </button>
        ) : null}
        <button data-testid="focus-close" onClick={onClose} className="pd-icon-button" title="End focus session" aria-label="End focus session"><X className="w-5 h-5" /></button>
      </div>
    </div>
  );
}
